import type React from 'react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import { clsJoin } from '@/utils'
import css from './styles.module.css'

interface Segment {
  value: string
  label: string
}

interface Props {
  segments: Segment[]
  onClick: (value: string) => void
  defaultIndex?: number
}

export const SegmentedControl: React.FC<Props> = ({
  segments,
  onClick,
  defaultIndex = 0,
}) => {
  const [activeIndex, setActiveIndex] = useState(defaultIndex)
  const [ready, setReady] = useState(false)

  const containerRef = useRef<HTMLDivElement | null>(null)
  const activeSegmentRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    setReady(true)
  }, [])

  useLayoutEffect(() => {
    const activeSegment = activeSegmentRef.current
    const container = containerRef.current
    if (!activeSegment || !container) {
      return
    }

    const { offsetWidth, offsetLeft } = activeSegment
    const { style } = container

    style.setProperty('--highlight-width', `${offsetWidth}px`)
    style.setProperty('--highlight-x-pos', `${offsetLeft}px`)
  }, [activeIndex, segments.length])

  const onInputChange = (value: string, index: number) => {
    setActiveIndex(index)
    onClick(value)
  }

  return (
    <div className={css.controls_container} ref={containerRef}>
      <div className={clsJoin(css.controls, ready && css.ready)}>
        {segments.map((item, i) => (
          <div
            key={item.value}
            className={clsJoin(css.segment, i === activeIndex && css.active)}
            ref={i === activeIndex ? activeSegmentRef : null}
          >
            <input
              type="radio"
              value={item.value}
              id={item.label}
              checked={i === activeIndex}
              className={css.input}
              onChange={() => onInputChange(item.value, i)}
            />
            <label htmlFor={item.label} className={css.label}>
              {item.label}
            </label>
          </div>
        ))}
      </div>
    </div>
  )
}
